import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { motion } from "framer-motion";
import { ELO_TIERS, getEloTier } from "../common/eloTierUtils";
import EloBadge from "../common/EloBadge.jsx";

const MotionBox = motion(Box);
const SEGMENTS = 8;

const EMPTY_RANK = {
    label: "",
    elo: 0,
    globalRank: "",
    imageUrl: null,
};

function useEloCounter(target) {
    const [display, setDisplay] = useState(0);
    const raf = useRef(null);

    useEffect(() => {
        const start = performance.now();
        const duration = 1400;

        const tick = (now) => {
            const progress = Math.min((now - start) / duration, 1);
            const ease = 1 - Math.pow(1 - progress, 3);
            setDisplay(Math.round(ease * target));
            if (progress < 1) raf.current = requestAnimationFrame(tick);
        };

        raf.current = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf.current);
    }, [target]);

    return display;
}

function parseGlobalRankParts(globalRank) {
    const str = String(globalRank || "").trim();
    const match = str.match(/^([^\d]+)\s*([\d.,]+\s*%?)$/i);
    if (match) {
        const numericPercent = Number(
            match[2]
                .replace(/\s/g, "")
                .replace("%", "")
                .replace(",", "."),
        );
        if (!Number.isFinite(numericPercent) || numericPercent <= 0 || numericPercent > 10) {
            return { prefix: "", percent: "" };
        }

        const roundedPercent = numericPercent >= 1
            ? Math.round(numericPercent)
            : Number(numericPercent.toFixed(numericPercent < 0.1 ? 2 : 1));
        return {
            prefix: match[1].trim().toUpperCase(),
            percent: `${roundedPercent}%`,
        };
    }
    return { prefix: str.toUpperCase(), percent: "" };
}

function getTierProgress(elo) {
    const tier = getEloTier(elo);
    const idx = ELO_TIERS.findIndex((t) => t.id === tier.id);
    const next = idx >= 0 ? ELO_TIERS[idx + 1] : null;
    if (!next) {
        return { progress: 1, eloToNext: 0, isMax: true };
    }
    const span = Math.max(1, next.min - tier.min);
    const progress = Math.min(1, Math.max(0, (Number(elo) - tier.min) / span));
    return {
        progress,
        eloToNext: Math.max(0, Math.round(next.min - Number(elo))),
        isMax: false,
    };
}

export default function RankCard({ rankData = EMPTY_RANK }) {
    const {
        elo,
        globalRank,
    } = rankData;

    const eloValue = Number(elo) || 0;
    const displayElo = useEloCounter(eloValue);
    const tier = useMemo(() => getEloTier(elo), [elo]);
    const { prefix: rankPrefix, percent: rankPercent } = parseGlobalRankParts(globalRank);
    const { progress, eloToNext, isMax } = getTierProgress(elo);
    const filledSegs = Math.round(progress * SEGMENTS);
    const accent = tier.border;
    const accentSoft = tier.text;

    return (
        <MotionBox
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
            sx={{
                position: "relative",
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                minHeight: { xs: 360, md: 390 },
                display: "flex",
                flexDirection: "column",
                borderRadius: "18px",
                overflow: "hidden",
                background: `
                    radial-gradient(
                        ellipse 85% 55% at 50% 38%,
                        ${alpha(accent, 0.055)} 0%,
                        transparent 68%
                    ),
                    linear-gradient(
                        145deg,
                        rgba(13,13,15,0.99) 0%,
                        rgba(8,8,10,1) 55%,
                        rgba(5,6,8,1) 100%
                    )
                `,
                border: `1px solid ${alpha(accent, 0.18)}`,
                boxShadow: `
                    inset 0 1px 0 rgba(255,255,255,0.035),
                    inset 0 -42px 80px rgba(0,0,0,0.34),
                    0 20px 50px rgba(0,0,0,0.28)
                `,
                p: {
                    xs: "1.35rem",
                    md: "1.55rem 1.65rem 1.45rem",
                },
                "&::before": {
                    content: '\"\"',
                    position: "absolute",
                    inset: "7px",
                    borderRadius: "13px",
                    border: `1px solid ${alpha(accent, 0.08)}`,
                    pointerEvents: "none",
                    zIndex: 0,
                },
                "&::after": {
                    content: '\"\"',
                    position: "absolute",
                    top: 0,
                    left: "8%",
                    right: "8%",
                    height: "1px",
                    background: `
                        linear-gradient(
                            90deg,
                            transparent,
                            ${alpha(accent, 0.18)},
                            ${alpha(accentSoft, 0.65)},
                            ${alpha(accent, 0.18)},
                            transparent
                        )
                    `,
                    boxShadow: `0 0 14px ${alpha(accent, 0.18)}`,
                    pointerEvents: "none",
                    zIndex: 2,
                },
            }}
        >
            <Box
                aria-hidden
                sx={{
                    position: "absolute",
                    width: "22rem",
                    height: "22rem",
                    left: "50%",
                    top: "47%",
                    transform: "translate(-50%, -50%)",
                    borderRadius: "50%",
                    border: `1px solid ${alpha(accent, 0.045)}`,
                    boxShadow: `
                        inset 0 0 70px ${alpha(accent, 0.025)}
                    `,
                    pointerEvents: "none",
                    zIndex: 0,
                    "&::before": {
                        content: '\"\"',
                        position: "absolute",
                        inset: "18%",
                        borderRadius: "50%",
                        border: `1px solid ${alpha(accent, 0.035)}`,
                    },
                }}
            />

            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    height: "100%",
                    minHeight: { xs: 330, md: 355 },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "1rem",
                        pb: "1rem",
                        borderBottom: "1px solid rgba(255,255,255,0.045)",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.45rem",
                            minWidth: 0,
                        }}
                    >
                        <Box
                            sx={{
                                width: 5,
                                height: 5,
                                flexShrink: 0,
                                background: accent,
                                clipPath: "polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)",
                                boxShadow: `0 0 8px ${alpha(accent, 0.28)}`,
                            }}
                        />
                        <Typography
                            sx={{
                                fontFamily: "'Orbitron', sans-serif",
                                fontWeight: 700,
                                fontSize: {
                                    xs: "0.74rem",
                                    md: "0.82rem",
                                },
                                letterSpacing: "0.16em",
                                color: accentSoft,
                                textTransform: "uppercase",
                                whiteSpace: "nowrap",
                                textShadow: "none",
                            }}
                        >
                            {tier.label}
                        </Typography>
                    </Box>

                    {(rankPrefix || rankPercent) ? (
                        <Typography
                            sx={{
                                display: "flex",
                                alignItems: "baseline",
                                fontSize: {
                                    xs: "0.58rem",
                                    md: "0.63rem",
                                },
                                fontWeight: 600,
                                letterSpacing: "0.1em",
                                color: "rgba(255,255,255,0.34)",
                                whiteSpace: "nowrap",
                                flexShrink: 0,
                                textTransform: "uppercase",
                            }}
                        >
                            {rankPrefix || "TOP"}{" "}
                            {rankPercent ? (
                                <Box
                                    component="span"
                                    sx={{
                                        color: accentSoft,
                                        fontWeight: 700,
                                    }}
                                >
                                    {rankPercent}
                                </Box>
                            ) : null}{" "}
                            GLOBAL
                        </Typography>
                    ) : null}
                </Box>

                <Box
                    sx={{
                        position: "relative",
                        flex: 1,
                        minHeight: { xs: 185, md: 205 },
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        pt: "1rem",
                        pb: "1.35rem",
                    }}
                >
                    <Box
                        aria-hidden
                        sx={{
                            position: "absolute",
                            width: "12rem",
                            height: "8rem",
                            left: "50%",
                            top: "48%",
                            transform: "translate(-50%, -50%)",
                            background: `
                                radial-gradient(
                                    ellipse,
                                    ${alpha(accent, 0.15)} 0%,
                                    ${alpha(accent, 0.055)} 38%,
                                    transparent 72%
                                )
                            `,
                            filter: "blur(20px)",
                            pointerEvents: "none",
                        }}
                    />

                    <Box
                        sx={{
                            position: "relative",
                            zIndex: 1,
                            transform: "scale(1.04)",
                        }}
                    >
                        <EloBadge
                            value={eloValue}
                            displayValue={String(displayElo)}
                            size="large"
                        />
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            mt: "0.8rem",
                            gap: "0.7rem",
                        }}
                    >
                        <Box
                            sx={{
                                width: 32,
                                height: "1px",
                                background: `linear-gradient(90deg, transparent, ${alpha(accent, 0.5)})`,
                            }}
                        />
                        <Typography
                            sx={{
                                fontSize: "0.62rem",
                                fontWeight: 700,
                                letterSpacing: "0.22em",
                                textTransform: "uppercase",
                                color: "rgba(255,255,255,0.4)",
                            }}
                        >
                            Elo Rating
                        </Typography>
                        <Box
                            sx={{
                                width: 32,
                                height: "1px",
                                background: `linear-gradient(90deg, ${alpha(accent, 0.5)}, transparent)`,
                            }}
                        />
                    </Box>
                </Box>

                <Box
                    sx={{
                        width: "100%",
                        height: "1px",
                        background: `
                            linear-gradient(
                                90deg,
                                transparent,
                                rgba(255,255,255,0.075) 12%,
                                rgba(255,255,255,0.075) 88%,
                                transparent
                            )
                        `,
                        mb: "1rem",
                    }}
                />

                <Box>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "1rem",
                            mb: "0.75rem",
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: "0.62rem",
                                fontWeight: 600,
                                letterSpacing: "0.12em",
                                textTransform: "uppercase",
                                color: "rgba(255,255,255,0.45)",
                            }}
                        >
                            Rank Progress
                        </Typography>

                        <Box
                            sx={{
                                px: "0.7rem",
                                py: "0.3rem",
                                borderRadius: "999px",
                                border: `1px solid ${alpha(accent, 0.22)}`,
                                background: alpha(accent, 0.025),
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: "0.58rem",
                                    fontWeight: 600,
                                    letterSpacing: "0.08em",
                                    textTransform: "uppercase",
                                    color: accentSoft,
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {isMax
                                    ? "Max tier reached"
                                    : `${eloToNext.toLocaleString()} Elo to next`}
                            </Typography>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                            height: "6px",
                        }}
                    >
                        {Array.from({ length: SEGMENTS }).map((_, i) => (
                            <Box
                                key={`seg-${i}`}
                                sx={{
                                    flex: 1,
                                    height: "100%",
                                    background:
                                        i < filledSegs
                                            ? `linear-gradient(
                                                90deg,
                                                ${alpha(accent, 0.58)},
                                                ${accentSoft}
                                              )`
                                            : "rgba(255,255,255,0.055)",
                                    clipPath:
                                        "polygon(5px 0, 100% 0, calc(100% - 5px) 100%, 0 100%)",
                                    boxShadow:
                                        i < filledSegs
                                            ? `0 0 8px ${alpha(accent, 0.12)}`
                                            : "none",
                                }}
                            />
                        ))}
                    </Box>
                </Box>
            </Box>
        </MotionBox>
    );
}
